import { createContext, useEffect, useReducer } from 'react';

// third-party
import { Chance } from 'chance';
import { jwtDecode } from 'jwt-decode';

// reducer - state management
import { LOGIN, LOGOUT } from 'contexts/auth-reducer/actions';
import authReducer from 'contexts/auth-reducer/auth';

// project imports
import Loader from '@components/Loader';
import axios from 'utils/axios';

const chance = new Chance();

// constant
const initialState = {
  isLoggedIn: false,
  isInitialized: false,
  isAuthenticated: false,
  role: null,
  user: null
};

const verifyToken = (serviceToken) => {
  if (!serviceToken) {
    return false;
  }
  const decoded = jwtDecode(serviceToken);
  /**
   * Property 'exp' does not exist on type '<T = unknown>(token: string, options?: JwtDecodeOptions | undefined) => T'.
   */
  return decoded.exp > Date.now() / 1000;
};

const setSession = (serviceToken) => {
  if (serviceToken) {
    localStorage.setItem('serviceToken', serviceToken);
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
  } else {
    localStorage.removeItem('serviceToken');
    delete axios.defaults.headers.common.Authorization;
  }
};
// ==============================|| JWT CONTEXT & PROVIDER ||============================== //

const JWTContext = createContext(null);

export const JWTProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    const init = async () => {
      try {
        const serviceToken = window.localStorage.getItem('serviceToken');
        if (serviceToken && verifyToken(serviceToken)) {
          setSession(serviceToken);
          const response = await axios.get('/api/account/me');
          const { result } = response.data;
          dispatch({
            type: LOGIN,
            payload: {
              isLoggedIn: true,
              isAuthenticated: true,
              user: result
            }
          });
        } else {
          dispatch({
            type: LOGOUT
          });
        }
      } catch (err) {
        console.error(err);
        dispatch({
          type: LOGOUT
        });
      }
    };

    init();
  }, []);

  const login = async (username, password) => {
    try {
      const response = await axios.post('/api/authentication/token', { username, password });
      const { token } = response.data;
      setSession(token);

      const meResponse = await axios.get('/api/account/me');
      const { result } = meResponse.data;

      dispatch({
        type: LOGIN,
        payload: {
          isLoggedIn: true,
          isAuthenticated: true,
          user: result
        }
      });
    } catch (error) {
      console.info(error);
      return error;
    }
  };

  const register = async (registrationDataOrUsername, passwordArg, firstNameArg, lastNameArg) => {
    // Support object payload and legacy positional args used by older auth screens.
    const isObjectPayload = typeof registrationDataOrUsername === 'object' && registrationDataOrUsername !== null;
    const payload = isObjectPayload
      ? registrationDataOrUsername
      : {
          email: registrationDataOrUsername,
          password: passwordArg,
          firstName: firstNameArg,
          lastName: lastNameArg,
          name: `${firstNameArg || ''} ${lastNameArg || ''}`.trim(),
          mobileNumber: ''
        };

    const id = chance.bb_pin();
    const response = await axios.post('/api/account/register', {
      id,
      firstName: payload.firstName,
      lastName: payload.lastName,
      email: payload.email,
      mobileNumber: payload.mobileNumber,
      name: payload.name,
      password: payload.password,
      // Keep username for backward compatibility if backend still expects it.
      username: payload.email
    });
    let users = response.data;

    if (window.localStorage.getItem('users') !== undefined && window.localStorage.getItem('users') !== null) {
      const localUsers = window.localStorage.getItem('users');
      users = [
        ...JSON.parse(localUsers),
        {
          id,
          username: payload.email,
          password: payload.password,
          name: payload.name || `${payload.firstName || ''} ${payload.lastName || ''}`.trim(),
          email: payload.email,
          mobileNumber: payload.mobileNumber,
          firstName: payload.firstName,
          lastName: payload.lastName
        }
      ];
    }

    window.localStorage.setItem('users', JSON.stringify(users));
  };

  const logout = () => {
    setSession(null);
    dispatch({ type: LOGOUT });
  };

  const resetPassword = async (email) => {
    await axios.post('/api/users/forgot-password', { email });
  };

  const resetPasswordWithToken = async ({ token, email, newPassword }) => {
    await axios.post('/api/users/reset-password', { token, email, newPassword });
  };

  const updateProfile = () => {};

  if (state.isInitialized !== undefined && !state.isInitialized) {
    return <Loader />;
  }

  return <JWTContext value={{ ...state, login, logout, register, resetPassword, resetPasswordWithToken, updateProfile }}>{children}</JWTContext>;
};

export default JWTContext;
